import React from 'react'
import Select, { createFilter } from 'react-select'

import {
  multiSelectStyles,
  selectTheme,
  Option,
  MultiValue,
  MenuList,
  formatOptionWithDescription,
  MENU_PORTAL_TARGET,
} from '../helpers/selectHelpers'

/**
 * @import import { MultiSelect } from '@/components/forms/MultiSelect'
 * @param {object} props
 * @param {boolean} [props.loading] Shows react-select's loading indicator
 * @param {object[]} [props.options] Options as { value, label, description? }, or { label, options } groups
 * @param {number} [props.fixedWidth] Caps the control and menu at 300px; any truthy value acts as a flag, the number itself is not applied
 * @param {boolean} [props.error] Critical border and ring, matching field error styling
 * @param {node} [props.placeholder] Text shown while nothing is selected
 * @param {node} [props.placeholderPill] Placeholder rendered in the condensed option-label style
 * @param {boolean} [props.condenseOptions] Collapses multiple selections into an "N selected" label; defaults to true
 * @param {boolean} [props.showFullOptions] Lets selected chips wrap and grow the control
 * @param {number} [props.maxUnCondensedOptions] Selection count above which values condense
 * @param {boolean} [props.disabled] Disables the control
 * @param {boolean} [props.isSelectAll] Shows an option-count header at the top of the menu
 * @param {boolean} [props.allowSelectAll] Condensed label reads "All" when every option is selected
 * @param {string} [props.label] Noun pluralized in the option-count header; defaults to "item"
 */
export const MultiSelect = ({
  loading,
  options = [],
  fixedWidth,
  error,
  placeholder,
  placeholderPill,
  condenseOptions = true,
  showFullOptions = false,
  maxUnCondensedOptions = 0,
  ...props
}) => {
  if (maxUnCondensedOptions) {
    condenseOptions = props.value?.length > maxUnCondensedOptions
  }
  const styles = multiSelectStyles(
    fixedWidth,
    condenseOptions,
    showFullOptions,
    props.isSelectAll,
    error
  )

  const components = {
    Option,
    MenuList,
    ...(condenseOptions && { MultiValue }),
  }

  return (
    <Select
      // Shared between the two MultiSelect components
      isMulti
      isClearable
      isSearchable
      closeMenuOnSelect={false}
      escapeClearsValue={false}
      hideSelectedOptions={false}
      backspaceRemovesValue={false}
      filterOption={createFilter({ ignoreAccents: false })}
      formatOptionLabel={formatOptionWithDescription}
      options={options}
      styles={styles}
      theme={(theme) => selectTheme(theme)}
      components={components}
      isLoading={loading}
      isDisabled={props.disabled}
      menuPortalTarget={MENU_PORTAL_TARGET}
      // Only used in this file
      placeholder={
        placeholderPill ? <div className="option-label">{placeholderPill}</div> : placeholder
      }
      allowSelectAll={props.allowSelectAll}
      {...props}
    />
  )
}
